import { Box, Button, Grid, SvgIcon, Tooltip, useMediaQuery } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { brandColors } from 'types/colors';
import { ReactComponent as ControllerIcon } from '../../assets/images/icons/controller.svg';
import { ReactComponent as DeviceListIcon } from '../../assets/images/icons/device-list-icon.svg';
import { ReactComponent as HeatmapIcon } from '../../assets/images/icons/heatmap.svg';
// import { ReactComponent as HomeIcon } from '../../assets/images/icons/Home.svg';
import HomeIcon from '@mui/icons-material/Home';
import { ReactComponent as OccupancyIcon } from '../../assets/images/icons/OCCUPANCY.svg';
import { setLoading } from 'stores/sensor/heatmapDataSlice';
import { useDispatch, useSelector } from 'react-redux';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { useRef, useState } from 'react';

const navBtnStyle = {
  border: `solid 2px ${brandColors.BLUE}`,
  marginTop: '.5rem',
  color: '#000',
  height: '3rem',
  width: '3rem',
  backgroundColor: '#f0f2f5',
  '&:hover': {
    backgroundColor: `${brandColors.BLUE}`,
    scale: '1.15',
    '&:hover .MuiSvgIcon-root': {
      color: '#fff',
    },
  },
  '@media (max-width:600px)': {
    '&:hover': {
      scale: '1',
    },
  },
};

const NavigationBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isColumnLayout = isSmallScreen;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEnergyHovered, setIsEnergyHovered] = useState(false);
  const energyAnimationRefs = useRef([]);

  // Get current user from token
  const currentUser = useSelector(state => state.sensors.currentUser);

  // Build URL segments, defaulting to 'all' for all segments
  const customerId = 'all';
  const building = 'all';
  const floor = 'all';
  const area = 'all';

  const handleHomePageClick = () => {
    if (isSmallScreen) setIsMenuOpen(false);
    navigate(`/home`);
  };

  const handleDataClick = () => {
    if (isSmallScreen) setIsMenuOpen(false);
    dispatch(setLoading(true));
    navigate(`/heat-map`);
  };

  const handleDevicesListClick = () => {
    if (isSmallScreen) setIsMenuOpen(false);
    navigate(`/sensors-list`);
  };

  const handleInsightsClick = () => {
    if (isSmallScreen) setIsMenuOpen(false);
    navigate(`/insights`);
  };

  const startEnergyAnimation = () => {
    setIsEnergyHovered(true);
    energyAnimationRefs.current.forEach(animation => animation?.beginElement());
  };

  const stopEnergyAnimation = () => {
    setIsEnergyHovered(false);
    energyAnimationRefs.current.forEach(animation => animation?.endElement());
  };

  const handleOccupancyClick = () => {
    if (isSmallScreen) setIsMenuOpen(false);
    navigate(`/occupancy/`);
  };

  const handleFloorsmapClick = () => {
    if (isSmallScreen) setIsMenuOpen(false);
    navigate(`/floors-map/`);
  };

  const handleControllerClick = () => {
    if (isSmallScreen) setIsMenuOpen(false);
    navigate(`/controllers-list/`);
  };

  const handleBuildingDemandClick = () => {
    if (isSmallScreen) setIsMenuOpen(false);
    navigate(`/building-demand/`);
  };

  const noScaleSx = isColumnLayout
    ? {
        scale: '1',
        '&:hover': {
          scale: '1',
        },
      }
    : {};
  const navTooltipProps = {
    slotProps: {
      popper: {
        sx: {
          zIndex: 40002,
        },
      },
    },
  };

  const navButtons = (
    <>
      <Tooltip {...navTooltipProps} title="Home page" placement="bottom" arrow>
        <Button
          sx={[
            navBtnStyle,
            currentPath.startsWith('/home')
              ? {
                  backgroundColor: brandColors.BLUE,
                }
              : { backgroundColor: '#f0f2f5' },
            !isColumnLayout &&
              currentPath.startsWith('/home') && {
                scale: '1.05',
              },
            noScaleSx,
            {
              '@keyframes home-jello-horizontal': {
                '0%, 25%, 100%': { transform: 'scale3d(1, 1, 1)' },
                '7.5%': { transform: 'scale3d(1.08, 0.92, 1)' },
                '10%': { transform: 'scale3d(0.92, 1.08, 1)' },
                '12.5%': { transform: 'scale3d(1.05, 0.95, 1)' },
                '16.25%': { transform: 'scale3d(0.98, 1.02, 1)' },
                '18.75%': { transform: 'scale3d(1.02, 0.98, 1)' },
              },
              '&:hover .home-nav-icon': {
                animation: 'home-jello-horizontal 3s ease-in-out infinite',
              },
            },
          ]}
          onClick={handleHomePageClick}>
          <SvgIcon
            className="home-nav-icon"
            sx={{
              color: currentPath.startsWith('/home') ? '#fff' : '#000',
              scale: '.9',
              transformBox: 'fill-box',
              transformOrigin: 'center',
              '&:hover': {
                color: currentPath === '/home' ? '#fff' : '#000',
              },
            }}>
            <HomeIcon className="HomeIconClass" />
          </SvgIcon>
        </Button>
      </Tooltip>
      <Tooltip {...navTooltipProps} title="Sensors list" placement="bottom" arrow>
        <Button
          className="data-btn"
          sx={[
            navBtnStyle,
            { backgroundColor: '#f0f2f5' },
            currentPath === '/sensors-list' && {
              backgroundColor: brandColors.BLUE,
            },
            !isColumnLayout &&
              currentPath === '/sensors-list' && {
                scale: '1.05',
              },
            noScaleSx,
            {
              '@keyframes sensor-reading-pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.45 },
              },
              '@keyframes sensor-offline-blink': {
                '0%, 55%, 100%': { opacity: 1 },
                '62%, 78%': { opacity: 0.12 },
                '70%, 88%': { opacity: 1 },
              },
              '&:hover .sensors-nav-icon g:not(:nth-of-type(4))': {
                animation: 'sensor-reading-pulse 1.4s ease-in-out infinite',
              },
              '&:hover .sensors-nav-icon g:nth-of-type(2)': {
                animationDelay: '0.18s',
              },
              '&:hover .sensors-nav-icon g:nth-of-type(3)': {
                animationDelay: '0.36s',
              },
              '&:hover .sensors-nav-icon g:nth-of-type(4)': {
                animation: 'sensor-offline-blink 2s steps(1, end) infinite',
              },
            },
          ]}
          onClick={handleDevicesListClick}>
          <SvgIcon
            className="sensors-nav-icon"
            sx={{
              color: currentPath === '/sensors-list' ? '#fff' : '#000',
              scale: '1.2',
              marginTop: '-4px',
              '&:hover': {
                color: currentPath === '/sensors-list' ? '#fff' : '#000',
              },
            }}>
            <DeviceListIcon className="HomeIconClass" />
          </SvgIcon>
        </Button>
      </Tooltip>

      {!isSmallScreen && (
        <>
          <Tooltip {...navTooltipProps} title="Heat map" placement="bottom" arrow>
            <Button
              className="data-btn"
              sx={[
                navBtnStyle,
                { backgroundColor: '#f0f2f5' },
                currentPath.startsWith('/heat-map') && {
                  backgroundColor: brandColors.BLUE,
                },
                !isColumnLayout &&
                  currentPath.startsWith('/heat-map') && {
                    scale: '1.05',
                  },
                noScaleSx,
                {
                  '@keyframes heatmap-cells-enter': {
                    '0%, 10%': { opacity: 0.12 },
                    '10.1%, 100%': { opacity: 1 },
                  },
                  '@keyframes heatmap-arrow-flow': {
                    '0%, 10%': { opacity: 0 },
                    '10.1%, 100%': { opacity: 1 },
                  },
                  '&:hover .heatmap-nav-icon path:nth-of-type(n + 2):nth-of-type(-n + 10)': {
                    animation: 'heatmap-cells-enter 3s steps(1, end) infinite backwards',
                  },
                  '&:hover .heatmap-nav-icon path:nth-of-type(n + 6):nth-of-type(-n + 8)': {
                    animationDelay: '0.15s',
                  },
                  '&:hover .heatmap-nav-icon path:nth-of-type(n + 9):nth-of-type(-n + 10)': {
                    animationDelay: '0.3s',
                  },
                  '&:hover .heatmap-nav-icon path:nth-of-type(1), &:hover .heatmap-nav-icon path:nth-of-type(n + 11)': {
                    animation: 'heatmap-arrow-flow 3s steps(1, end) infinite backwards',
                  },
                  '&:hover .heatmap-nav-icon path:nth-of-type(11)': {
                    animationDelay: '0.15s',
                  },
                  '&:hover .heatmap-nav-icon path:nth-of-type(1)': {
                    animationDelay: '0.3s',
                  },
                },
              ]}
              onClick={handleDataClick}>
              <SvgIcon
                className="heatmap-nav-icon"
                sx={{
                  color: currentPath.startsWith('/heat-map') ? '#fff' : '#000',
                  scale: '1.4',
                  marginTop: '-4px',
                  '&:hover': {
                    color: currentPath.startsWith('/heat-map') ? '#fff' : '#000',
                  },
                }}>
                <HeatmapIcon />
              </SvgIcon>
            </Button>
          </Tooltip>

          {/* <Tooltip {...navTooltipProps} title="Energy saving" placement="bottom" arrow>
            <Button
              className="data-btn"
              sx={[
                navBtnStyle,
                { backgroundColor: '#f0f2f5' },
                currentPath.startsWith('/insights') && {
                  backgroundColor: brandColors.BLUE,
                },
                !isColumnLayout &&
                  currentPath.startsWith('/insights') && {
                    scale: '1.05',
                  },
                noScaleSx,
              ]}
              onMouseEnter={startEnergyAnimation}
              onMouseLeave={stopEnergyAnimation}
              onClick={handleInsightsClick}>
              <SvgIcon
                className="energy-flower-icon"
                viewBox="0 0 41.3 41.3"
                shapeRendering="geometricPrecision"
                sx={{
                  color: currentPath.startsWith('/insights') ? '#fff' : '#000',
                  scale: '1.4',
                  marginTop: '-4px',
                  '&:hover': {
                    color: currentPath.startsWith('/insights') ? '#fff' : '#000',
                  },
                }}>
                <defs>
                  <path
                    id="energy-flower-stem"
                    d="M25.6 21.5c-1.8.6-3.3 2.1-3.7 4 1.6-1.2 3.6-1.7 5.6-1.5-3.1.2-5.2 1.7-6.4 3.7l-.2-6.7c1.4-.2 2.4-1.3 2.4-2.7v-3.5h-5.5v3.5c0 1.4 1.1 2.6 2.4 2.7l-.2 6.7c-1.2-2-3.2-3.5-6.4-3.7 2-.2 4 .3 5.6 1.5-.4-1.9-1.9-3.4-3.7-4-1.8-.6-4.4 0-6.1 1.1.6.3 1.1.9 1.6 1.5.5.7 1 1.5 1.6 2.1.8.8 1.8 1.2 2.9 1.3 1 0 2-.1 2.9-.6.7.9 1.3 2 1.6 3.2v2.7h1.3v-2.7c.2-1.2.7-2.2 1.5-3.2.9.5 1.9.7 2.9.6 1.1-.1 2.1-.6 2.9-1.3.6-.6 1.1-1.4 1.6-2.1.4-.6.9-1.1 1.6-1.5-1.6-1.2-4.3-1.7-6.1-1.1Z"
                  />
                  <clipPath id="energy-stem-clip">
                    <rect x="0" y="0" width="41.3" height="21.2" />
                    <rect x="19.8" y="20" width="1.7" height="21.3" />
                  </clipPath>
                </defs>
                <use href="#energy-flower-stem" clipPath="url(#energy-stem-clip)" />
                <path className="energy-petal-left" d="M20.2 27.7c-1.2-2-3.2-3.5-6.4-3.7 2-.2 4 .3 5.6 1.5-.4-1.9-1.9-3.4-3.7-4-1.8-.6-4.4 0-6.1 1.1.6.3 1.1.9 1.6 1.5.5.7 1 1.5 1.6 2.1.8.8 1.8 1.2 2.9 1.3 1 0 2-.1 2.9-.6.7.9 1.3 2 1.6 3.2l.5-2.4Z">
                  <animateTransform
                    ref={element => {
                      energyAnimationRefs.current[0] = element;
                    }}
                    attributeName="transform"
                    type="rotate"
                    begin="indefinite"
                    dur="0.8s"
                    repeatCount="1"
                    values="0 20.2 27.7;-12 20.2 27.7;2 20.2 27.7;0 20.2 27.7"
                    keyTimes="0;0.2;0.55;1"
                  />
                </path>
                <path className="energy-petal-right" d="M21.1 27.7c1.2-2 3.2-3.5 6.4-3.7-2-.2-4 .3-5.6 1.5.4-1.9 1.9-3.4 3.7-4 1.8-.6 4.4 0 6.1 1.1-.6.3-1.1.9-1.6 1.5-.5.7-1 1.5-1.6 2.1-.8.8-1.8 1.2-2.9 1.3-1 0-2-.1-2.9-.6-.7.9-1.3 2-1.6 3.2l-.5-2.4Z">
                  <animateTransform
                    ref={element => {
                      energyAnimationRefs.current[1] = element;
                    }}
                    attributeName="transform"
                    type="rotate"
                    begin="indefinite"
                    dur="0.8s"
                    repeatCount="1"
                    values="0 21.1 27.7;12 21.1 27.7;-2 21.1 27.7;0 21.1 27.7"
                    keyTimes="0;0.2;0.55;1"
                  />
                </path>
                <path
                  className="energy-bolt"
                  style={{
                    fill: isEnergyHovered ? brandColors.BLUE : currentPath.startsWith('/insights') ? brandColors.BLACK : '#fff',
                  }}
                  d="M20.3,19.4v-1.2c0,0-.7,0-.7,0,0,0-.1,0,0-.1l1-1.9c0,0,.2,0,.2,0v1.2c0,0,.7,0,.7,0,0,0,.1,0,0,.1l-1,1.9c0,0-.2,0-.2,0Z"
                />
                <g className="energy-plug">
                  <rect x="17.1" y="12.8" width="6.8" height="1.6" rx=".3" ry=".3" />
                  <path d="M19.4 8.8a.5.5 0 0 0-1 0v3.4h1.1V8.8Z">
                    <animate
                      ref={element => {
                        energyAnimationRefs.current[2] = element;
                      }}
                      attributeName="opacity"
                      begin="indefinite"
                      dur="1.2s"
                      repeatCount="1"
                      values="1;1;.15;1;.15;1;1"
                      keyTimes="0;.46;.52;.58;.64;.7;1"
                    />
                  </path>
                  <path d="M22.7 8.8a.5.5 0 0 0-1 0v3.4h1.1V8.8Z">
                    <animate
                      ref={element => {
                        energyAnimationRefs.current[3] = element;
                      }}
                      attributeName="opacity"
                      begin="indefinite"
                      dur="1.2s"
                      repeatCount="1"
                      values="1;1;.15;1;.15;1;1"
                      keyTimes="0;.46;.52;.58;.64;.7;1"
                    />
                  </path>
                </g>
              </SvgIcon>
            </Button>
          </Tooltip> */}

          <Tooltip {...navTooltipProps} title="Occupancy rates" placement="bottom" arrow>
            <Button
              className="data-btn"
              sx={[
                navBtnStyle,
                { backgroundColor: '#f0f2f5' },
                currentPath.startsWith('/occupancy') && {
                  backgroundColor: brandColors.BLUE,
                },
                !isColumnLayout &&
                  currentPath.startsWith('/occupancy') && {
                    scale: '1.05',
                  },
                noScaleSx,
                {
                  '@keyframes occupancy-left-arrive': {
                    '0%': { transform: 'translateX(-8px)' },
                    '18%, 100%': { transform: 'translateX(0)' },
                  },
                  '@keyframes occupancy-right-arrive': {
                    '0%': { transform: 'translateX(8px)' },
                    '18%, 100%': { transform: 'translateX(0)' },
                  },
                  '&:hover .occupancy-nav-icon path:nth-of-type(n + 5)': {
                    transformBox: 'fill-box',
                    transformOrigin: 'center bottom',
                    animation: 'occupancy-left-arrive 3s ease-out infinite',
                  },
                  '&:hover .occupancy-nav-icon path:nth-of-type(n + 3):nth-of-type(-n + 4)': {
                    transformBox: 'fill-box',
                    transformOrigin: 'center bottom',
                    animation: 'occupancy-right-arrive 3s ease-out infinite',
                  },
                },
              ]}
              onClick={handleOccupancyClick}>
              <SvgIcon
                className="occupancy-nav-icon"
                sx={{
                  color: currentPath.startsWith('/occupancy') ? '#fff' : '#000',
                  scale: '1.2',
                  marginTop: '-4px',
                  '&:hover': {
                    color: currentPath.startsWith('/occupancy') ? '#fff' : '#000',
                  },
                }}>
                <OccupancyIcon className="OccupancyIconClass" />
              </SvgIcon>
            </Button>
          </Tooltip>
        </>
      )}

      <Tooltip {...navTooltipProps} title="Floors Map" placement="bottom" arrow>
        <Button
          className="data-btn"
          sx={[
            navBtnStyle,
            { backgroundColor: '#f0f2f5' },
            currentPath.startsWith('/floors-map') && {
              backgroundColor: brandColors.BLUE,
              ...(isColumnLayout ? {} : { scale: '1.05' }),
            },
            noScaleSx,
          ]}
          onClick={handleFloorsmapClick}>
          <SvgIcon
            className="floors-map-nav-icon"
            viewBox="0 0 106 123"
            sx={{
              color: currentPath.startsWith('/floors-map') ? '#fff' : '#000',
              scale: '0.8',
              marginTop: '-4px',
              '@keyframes map-pin-drop': {
                '0%': { transform: 'translateY(-12px)' },
                '12%': { transform: 'translateY(2px)' },
                '20%, 100%': { transform: 'translateY(0)' },
              },
              '@keyframes map-unfold': {
                '0%': { opacity: 0.45, transform: 'scaleX(0.55)' },
                '12%': { opacity: 1, transform: 'scaleX(1.06)' },
                '20%, 100%': { opacity: 1, transform: 'scaleX(1)' },
              },
              '&:hover .map-pin': {
                transformBox: 'fill-box',
                transformOrigin: 'center bottom',
                animation: 'map-pin-drop 3s ease-out infinite',
              },
              '&:hover .map-fold': {
                transformBox: 'fill-box',
                transformOrigin: 'center bottom',
                animation: 'map-unfold 3s ease-out infinite',
              },
              '&:hover': {
                color: currentPath.startsWith('/floors-map') ? '#fff' : '#000',
              },
            }}>
            <g className="map-fold" fill="currentColor">
              <path d="M37.92 73.88V101.7l30.15 13V78.87a65.17 65.17 0 0 0 6.45-5.63v41.18l25-12.59v-56l-9.61 3.7a61.61 61.61 0 0 0 2.38-7.81l9.3-3.59A3.22 3.22 0 0 1 105.7 40a3.18 3.18 0 0 1 .22 1.16v62.7a3.23 3.23 0 0 1-2 3l-31.2 15.67a3.23 3.23 0 0 1-2.92 0l-35-15.17-30.12 15.17a3.22 3.22 0 0 1-4.33-1.42A3.28 3.28 0 0 1 0 119.66V53.24a3.23 3.23 0 0 1 2.32-3.1l16.38-6.32a58.63 58.63 0 0 0 2.16 6.07L6.46 55.44v59l25-12.59V67.09a76.28 76.28 0 0 0 6.46 6.79Z" />
            </g>
            <g className="map-pin" fill="currentColor" fillRule="evenodd">
              <path d="M56.92 73.14a1.62 1.62 0 0 1-1.86.06A65.25 65.25 0 0 1 38.92 58.8 51.29 51.29 0 0 1 28.06 35.37C26.77 27.38 28 19.7 32 13.45a27 27 0 0 1 6-6.66A29.23 29.23 0 0 1 56.36 0 26 26 0 0 1 73.82 7.12a26 26 0 0 1 4.66 5.68c4.27 7 5.19 16 3.31 25.12a55.29 55.29 0 0 1-24.87 35.22ZM55.15 14.21a13.72 13.72 0 1 1 0 27.44 13.72 13.72 0 0 1 0-27.44Z" />
            </g>
          </SvgIcon>
        </Button>
      </Tooltip>
      <Tooltip {...navTooltipProps} title="Controllers list" placement="bottom" arrow>
        <Button
          // className="data-btn"
          sx={{
            marginTop: '.5rem',
            border: `solid 2px ${brandColors.BLUE}`,
            height: '3rem',
            width: '3rem',
            scale: !isColumnLayout && currentPath.startsWith('/controllers') ? '1.05' : '1',
            backgroundColor: currentPath.startsWith('/controllers') ? `${brandColors.TURQUOISE}` : '#f0f2f5',
            '&:hover': {
              border: `solid 2px #000`,
              backgroundColor: `${brandColors.TURQUOISE}`,
              scale: isColumnLayout ? '1' : '1.15',
              '&:hover .MuiSvgIcon-root': {
                color: '#000',
              },
            },
            '@media (max-width:600px)': {
              '&:hover': {
                scale: '1',
              },
            },
            '@keyframes fan-speed-1': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' },
            },
            '@keyframes fan-speed-2': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' },
            },
            '@keyframes fan-speed-3': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' },
            },
            '@keyframes fan-speed-4': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' },
            },
            '& .controller-nav-icon': {
              transformOrigin: '50% 50%',
            },
            '&:hover .controller-nav-icon': {
              animation:
                'fan-speed-1 0.8s linear 0s 1, fan-speed-2 0.3s linear 0.8s 1, fan-speed-3 0s linear 1.1s 1, fan-speed-4 0.4s linear 1.1s infinite',
            },
          }}
          onClick={handleControllerClick}>
          <SvgIcon
            className="controller-nav-icon"
            sx={[
              currentPath.startsWith('/controllers') &&
                {
                  // backgroundColor: brandColors.BLUE,
                },
              {
                scale: '1.15',
                color: brandColors.BLACK,
                '&:hover': {
                  scale: '1.15',
                  color: currentPath.startsWith('/controllers') ? brandColors.RED : '#000',
                },
              },
            ]}>
            <ControllerIcon className="controllers" />
          </SvgIcon>
        </Button>
      </Tooltip>
      <Tooltip {...navTooltipProps} title="Building demand" placement="bottom" arrow>
        <Button
          sx={[
            navBtnStyle,
            currentPath.startsWith('/building-demand') && {
              backgroundColor: brandColors.BLUE,
              ...(isColumnLayout ? {} : { scale: '1.05' }),
            },
            noScaleSx,
            {
              '@keyframes building-window-on': {
                '0%, 7%, 11.1%, 15.1%, 19.1%, 100%': { opacity: 1, fill: brandColors.BLUE },
                '7.1%, 11%': { opacity: 1, fill: '#fff' },
                '15.1%, 19%': { opacity: 1, fill: '#fff' },
              },
              '&:hover .building-demand-nav-icon .building-demand-light': {
                animation: 'building-window-on 3s steps(1, end) infinite backwards',
              },
            },
          ]}
          onClick={handleBuildingDemandClick}>
          <SvgIcon
            className="building-demand-nav-icon"
            sx={{
              color: currentPath.startsWith('/building-demand') ? '#fff' : '#000',
              scale: '0.85',
              '&:hover': {
                color: currentPath.startsWith('/building-demand') ? '#fff' : '#000',
              },
            }}>
            <path d="M17 11V3H7v4H3v14h8v-4h2v4h8V11zM7 19H5v-2h2zm0-4H5v-2h2zm0-4H5V9h2zm4 4H9v-2h2zm0-4H9V9h2zm0-4H9V5h2zm4 8h-2v-2h2zm0-4h-2V9h2zm0-4h-2V5h2zm4 12h-2v-2h2zm0-4h-2v-2h2z" />
            <g className="building-demand-light building-demand-light--top" fill={brandColors.BLUE} opacity="0">
              <rect x="9" y="5" width="2" height="2" />
              <rect x="13" y="5" width="2" height="2" />
            </g>
            <g className="building-demand-light building-demand-light--middle" fill={brandColors.BLUE} opacity="0">
              <rect x="5" y="9" width="2" height="2" />
              <rect x="9" y="9" width="2" height="2" />
              <rect x="13" y="9" width="2" height="2" />
            </g>
            <g className="building-demand-light building-demand-light--bottom" fill={brandColors.BLUE} opacity="0">
              <rect x="5" y="13" width="2" height="2" />
              <rect x="9" y="13" width="2" height="2" />
              <rect x="13" y="13" width="2" height="2" />
              <rect x="17" y="13" width="2" height="2" />
              <rect x="5" y="17" width="2" height="2" />
              <rect x="17" y="17" width="2" height="2" />
            </g>
          </SvgIcon>
        </Button>
      </Tooltip>
    </>
  );

  return (
    <>
      {isSmallScreen && isMenuOpen ? (
        <Box
          onClick={() => setIsMenuOpen(false)}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 30000,
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
          }}
        />
      ) : null}

      <Box
        className="navbar"
        sx={{
          position: { xs: 'fixed', sm: 'absolute' },
          left: { xs: '1.5rem', sm: '50%' },
          paddingTop: { xs: 0, sm: '3rem' },
          display: 'flex',
          justifyContent: 'center',
          transform: { xs: 'none', sm: 'translateX(-50%)' },
          transition: 'transform 0.2s cubic-bezier(0.47, 0.54, 0.32, 1.28)',
          '&:hover': {
            transform: isSmallScreen ? 'none' : 'translateX(-50%) scale(1.08)',
          },
          top: { xs: '.5rem', sm: '-3rem' },
          mt: 0,
          zIndex: 30001,
          backgroundColor: 'transparent',
        }}>
        {!isSmallScreen ? (
          <Grid
            item
            sx={{
              display: 'flex',
              width: 'fit-content',
              gap: '.5rem',
            }}>
            {navButtons}
          </Grid>
        ) : (
          <Grid
            item
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '.5rem',
              width: 'fit-content',
            }}>
            <Button
              sx={{
                ...navBtnStyle,
                backgroundColor: '#f0f2f5',
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.24)',
                '&:hover': {
                  ...navBtnStyle['&:hover'],
                  boxShadow: '0 8px 22px rgba(0, 0, 0, 0.3)',
                },
              }}
              onClick={() => setIsMenuOpen(prev => !prev)}>
              <SvgIcon sx={{ color: '#000' }}>{isMenuOpen ? <CloseIcon /> : <MenuIcon />}</SvgIcon>
            </Button>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '.5rem',
                overflow: 'hidden',
                maxHeight: isMenuOpen ? '28rem' : 0,
                opacity: isMenuOpen ? 1 : 0,
                transform: isMenuOpen ? 'translateY(0)' : 'translateY(-8px)',
                transition: 'max-height 280ms ease, opacity 220ms ease, transform 220ms ease',
              }}>
              {navButtons}
            </Box>
          </Grid>
        )}
      </Box>
    </>
  );
};

export default NavigationBar;
